import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { listUsers, type UserSummary, type UserListFilters } from "@/api/adminApi";

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200] as const;

type SortField = "created_at" | "last_sign_in_at" | "available" | "total_spent";

const FILTER_BUTTONS: { key: keyof UserListFilters; label: string }[] = [
  { key: "has_purchased", label: "Has Purchased" },
  { key: "zero_balance", label: "Zero Balance" },
  { key: "missing_welcome_credits", label: "Missing Welcome Credits" },
  { key: "is_beta", label: "Beta" },
  { key: "inactive_30d", label: "Inactive 30d+" },
];

function formatDate(d: string | null): string {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatCurrency(cents: number): string {
  if (cents === 0) return "—";
  return "$" + (cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function SortArrow({ field, sortBy, sortDir }: { field: SortField; sortBy: SortField; sortDir: string }) {
  if (field !== sortBy) return <span className="sort-arrow muted">&updownarrow;</span>;
  return <span className="sort-arrow">{sortDir === "asc" ? "\u25B2" : "\u25BC"}</span>;
}

export function UsersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [sortBy, setSortBy] = useState<SortField>("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [filters, setFilters] = useState<UserListFilters>(() => {
    // Accept ?missing_welcome_credits=1 from dashboard deeplink
    const init: UserListFilters = {};
    if (searchParams.get("missing_welcome_credits") === "1") init.missing_welcome_credits = true;
    return init;
  });
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listUsers(page * pageSize, pageSize, sortBy, sortDir, filters);
      setUsers(res.users);
      setTotal(res.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sortBy, sortDir, filters]);

  useEffect(() => { load(); }, [load]);

  // Debounced search
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(0);
      setFilters((f) => {
        const next = { ...f };
        if (value.trim()) next.search = value.trim();
        else delete next.search;
        return next;
      });
    }, 300);
  };

  const toggleFilter = (key: keyof UserListFilters) => {
    setPage(0);
    setFilters((f) => {
      const next = { ...f };
      if (next[key]) delete next[key];
      else (next as Record<string, boolean>)[key] = true;
      return next;
    });
    // Clear the deeplink param once user manually toggles filters
    if (searchParams.has("missing_welcome_credits")) {
      const sp = new URLSearchParams(searchParams);
      sp.delete("missing_welcome_credits");
      setSearchParams(sp, { replace: true });
    }
  };

  const handleSort = (field: SortField) => {
    if (field === sortBy) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDir("desc");
    }
    setPage(0);
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const showing = users.length > 0
    ? `${page * pageSize + 1}\u2013${page * pageSize + users.length} of ${total}`
    : "0";

  return (
    <div>
      {/* Search + Filters */}
      <div className="filter-bar">
        <input
          className="input-field"
          type="text"
          placeholder="Search by email or name..."
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          style={{ flex: 1, maxWidth: 340 }}
        />
        {FILTER_BUTTONS.map((f) => (
          <button
            key={f.key}
            className={`range-btn ${filters[f.key] ? "active" : ""}`}
            onClick={() => toggleFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && <div className="loading">Searching the archives...</div>}

      {!loading && users.length === 0 && (
        <div className="empty-state">No patrons found</div>
      )}

      {!loading && users.length > 0 && (
        <>
          <div className="card">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th className="sort-header" onClick={() => handleSort("created_at")}>
                    Joined <SortArrow field="created_at" sortBy={sortBy} sortDir={sortDir} />
                  </th>
                  <th className="sort-header" onClick={() => handleSort("last_sign_in_at")}>
                    Last Active <SortArrow field="last_sign_in_at" sortBy={sortBy} sortDir={sortDir} />
                  </th>
                  <th className="sort-header" onClick={() => handleSort("available")}>
                    Credits <SortArrow field="available" sortBy={sortBy} sortDir={sortDir} />
                  </th>
                  <th>Used</th>
                  <th className="sort-header" onClick={() => handleSort("total_spent")}>
                    Spent <SortArrow field="total_spent" sortBy={sortBy} sortDir={sortDir} />
                  </th>
                  <th>Beta</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="clickable" onClick={() => navigate(`/users/${u.id}`)}>
                    <td>{u.display_name || "—"}</td>
                    <td>{u.email}</td>
                    <td>{formatDate(u.created_at)}</td>
                    <td>{formatDate(u.last_sign_in_at)}</td>
                    <td title={u.has_balance_row ? undefined : "No ai_token_balances row — welcome credits never granted"}>
                      {u.has_balance_row
                        ? u.credits.toLocaleString()
                        : <span style={{ color: "var(--danger, #e04040)" }}>— missing</span>}
                    </td>
                    <td>{u.has_balance_row ? u.used.toLocaleString() : "—"}</td>
                    <td>{formatCurrency(u.total_spent)}</td>
                    <td>{u.is_beta ? <span className="badge badge-gold">Beta</span> : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination">
            <span className="pagination-info">Showing {showing}</span>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <label style={{ fontSize: 11, color: "#9A8E7A" }}>
                Per page:{" "}
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
                  style={{
                    background: "#161616",
                    color: "#F0E8D8",
                    border: "1px solid #4A4540",
                    padding: "2px 6px",
                    borderRadius: 3,
                    marginLeft: 4,
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
              <button
                className="btn-outline"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
              >
                &larr; Prev
              </button>
              <button
                className="btn-outline"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
              >
                Next &rarr;
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
